import * as React from "react";
import { omit } from "ramda";
import { Selection, type IObjectWithKey } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { SiteSortKey, Site } from "../../api";
import { type SitesApiResource, type SitesQuery } from "../../domain/repositories/repository-sites";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SelectionState } from "../../utils/selection-state";
import { defaultPageSize } from "../../utils/constants";

export interface ListSitesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListSiteItem extends IObjectWithKey {
    readonly label: string;
    readonly createdAt: string;
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListSites extends React.Component<ListSitesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListSitesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.sites.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }
    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<SiteSortKey> {
        return store.serviceListStates.sites.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<SitesApiResource> {
        return store.serviceListStates.sites.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.sites.getSelectionState(this.listStateId);
    }

    @computed private get query(): SitesQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get sites(): (Site | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositorySites.byId(id));
        }
        return store.repositorySites.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListSiteItem | undefined)[] {
        return this.sites.map((site) => {
            if (!site) {
                return;
            }
            return {
                label: site.label,
                createdAt: store.i18n.formatDateOnly(site.createdAt),
                key: site.id,
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.sites.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns([
                        {
                            fieldName: "label",
                            name: store.i18n.t("listSites.column.label.name"),
                            key: "label",
                            minWidth: 100,
                        },
                        {
                            fieldName: "createdAt",
                            name: store.i18n.t("listSites.column.createdAt.name"),
                            key: "createdAt",
                            minWidth: 100,
                        },
                    ])}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
